import { Activity, Bot, DollarSign, FlaskConical, Pause, Play, UserPlus, Zap } from 'lucide-react'
import { useEffect, useState } from 'react'
import { API_URL } from '../../config/api'

interface TimelineEvent {
  id: number
  event_type?: string
  type?: string
  event_data?: Record<string, unknown>
  data?: string
  agent_name?: string | null
  user_name?: string | null
  created_at: string
}

interface ProjectTimelineProps { projectId: number; isResearch?: boolean }

const EVENT_CONFIG: Record<string, { icon: React.ReactNode; color: string; label: string }> = {
  research_started:   { icon: <FlaskConical size={14} />, color: '#10b981', label: 'Research Started' },
  research_paused:    { icon: <Pause size={14} />,        color: '#f59e0b', label: 'Paused' },
  research_resumed:   { icon: <Play size={14} />,         color: '#10b981', label: 'Resumed' },
  donation_received:  { icon: <DollarSign size={14} />,   color: '#6366f1', label: 'Donation' },
  agent_joined:       { icon: <UserPlus size={14} />,     color: '#3b82f6', label: 'Agent Joined' },
  baba_assigned:      { icon: <Bot size={14} />,          color: '#8b5cf6', label: 'Experts Assigned' },
  expert_sync:        { icon: <Zap size={14} />,          color: '#f59e0b', label: 'Expert Sync' },
  finding_added:      { icon: <FlaskConical size={14} />, color: '#10b981', label: 'Finding Added' },
  cycle_completed:    { icon: <Activity size={14} />,     color: '#06b6d4', label: 'Cycle Completed' },
}

function getEventConfig(type: string) {
  return EVENT_CONFIG[type] || { icon: <Activity size={14} />, color: 'var(--secondary)', label: (type || 'event').replace(/_/g, ' ') }
}

function formatEventDesc(event: TimelineEvent): string {
  const d = event.event_data || {}
  switch (event.event_type) {
    case 'research_paused':
      return `Paused — ${String(d.reason as string || 'budget depleted')}`
    case 'research_resumed':
      return 'Research resumed'
    case 'donation_received':
      return `${Number(d.amount_flz || 0).toLocaleString()} FLZ received`
    case 'agent_joined':
      return `${event.agent_name || `Agent #${String(d.agent_id as number)}`} joined as ${String(d.role as string || 'contributor')}`
    case 'baba_assigned':
      return `Experts assigned: ${Array.isArray(d.experts) ? (d.experts as number[]).join(', ') : 'multiple'}`
    case 'expert_sync':
      return `${Number(d.findings_synthesized || 0)} findings synthesized by ${Number(d.experts_participated || 0)} experts`
    case 'research_started':
      return 'Research project initialized'
    default:
      return Object.entries(d).map(([k, v]) => `${k}: ${String(v)}`).join(' · ').slice(0, 120) || event.event_type || 'event'
  }
}

export default function ProjectTimeline({ projectId, isResearch }: ProjectTimelineProps) {
  const [events, setEvents] = useState<TimelineEvent[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(isResearch ? `${API_URL}/api/research/projects/${projectId}/events` : `${API_URL}/api/projects/${projectId}/events`)
        const data = await res.json() as { events: TimelineEvent[] }
        // Backend returns {type, data} but we expect {event_type, event_data}
        const normalized = (data.events || []).map(e => ({
          ...e,
          event_type: e.event_type || e.type || 'finding',
          event_data: e.event_data || (typeof e.data === 'string' ? { content: e.data } : {})
        }))
        setEvents(normalized)
      } catch {
        // silent
      } finally {
        setLoading(false)
      }
    }
    void load()
  }, [projectId])

  if (loading) {return <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-secondary)' }}>Loading...</div>}

  if (events.length === 0) {return (
    <div style={{ padding: 60, textAlign: 'center', color: 'var(--text-secondary)' }}>
      <Activity size={40} style={{ opacity: 0.3, marginBottom: 12 }} />
      <p style={{ margin: 0 }}>No timeline events yet</p>
    </div>
  )}

  return (
    <div style={{ padding: '20px 0' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 24, padding: '0 4px' }}>
        <Activity size={18} style={{ color: 'var(--primary-color)' }} />
        <span style={{ fontWeight: 600, fontSize: 16 }}>Timeline</span>
        <span style={{
          background: 'var(--border-color)', color: 'var(--text-secondary)',
          borderRadius: 20, padding: '1px 8px', fontSize: 12
        }}>{events.length}</span>
      </div>

      <div style={{ position: 'relative', paddingLeft: 32 }}>
        {/* vertical line */}
        <div style={{
          position: 'absolute', left: 11, top: 0, bottom: 0,
          width: 2, background: 'var(--border-color)'
        }} />

        {events.map((ev, i) => {
          const cfg = getEventConfig(ev.event_type || 'finding')
          return (
            <div key={ev.id} style={{ position: 'relative', marginBottom: i < events.length - 1 ? 20 : 0 }}>
              {/* dot */}
              <div style={{
                position: 'absolute', left: -21, top: 12,
                width: 22, height: 22, borderRadius: '50%',
                background: cfg.color + '20', border: `2px solid ${cfg.color}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: cfg.color
              }}>
                {cfg.icon}
              </div>

              <div style={{
                background: 'var(--card-bg)', border: '1px solid var(--border-color)',
                borderRadius: 10, padding: '10px 14px'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                  <span style={{
                    fontSize: 12, fontWeight: 600, color: cfg.color,
                    textTransform: 'capitalize'
                  }}>
                    {cfg.label}
                  </span>
                  <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
                    {new Date(ev.created_at).toLocaleString('en-US', {
                      month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
                    })}
                  </span>
                </div>
                <p style={{ margin: 0, fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                  {formatEventDesc(ev)}
                </p>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
